import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { OrdersTable } from '../tables/OrdersTable';

interface PastOrdersDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  pharmacyId: string;
  highlightRxNumbers?: string[]; // Rx numbers to highlight as duplicates
}

/**
 * PastOrdersDialog
 *
 * Displays complete patient order history with Rx number highlighting
 * Reuses the OrdersTable component for consistency
 *
 * Features:
 * - Shows all patient orders with search/filter capabilities
 * - Highlights duplicate Rx numbers (passed via highlightRxNumbers prop)
 * - Quick navigation to order details
 * - Full Material-UI Dialog with responsive design
 */
const PastOrdersDialog: React.FC<PastOrdersDialogProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  pharmacyId,
  highlightRxNumbers = []
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch patient orders when dialog opens
  useEffect(() => {
    if (open && patientId && pharmacyId) {
      fetchOrders();
    }
  }, [open, patientId, pharmacyId]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/orders/${pharmacyId}`, {
        params: {
          patientId: patientId,
          limit: 100, // Fetch last 100 orders
        }
      });

      const fetchedOrders = response.data.data || response.data || [];
      setOrders(fetchedOrders);
    } catch (error: any) {
      console.error('Failed to fetch patient orders:', error);
      enqueueSnackbar('Failed to load order history', { variant: 'error' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Enhance orders with highlight flag for matching Rx numbers
  const ordersWithHighlight = orders.map(order => {
    const hasHighlightedRx = order.items?.some((item: any) =>
      highlightRxNumbers.includes(item.rxNumber)
    );

    return {
      ...order,
      _highlighted: hasHighlightedRx
    };
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="div">
              Order History - {patientName}
            </Typography>
            {highlightRxNumbers.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Highlighting {highlightRxNumbers.length} Rx number(s) from new order
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No orders found for this patient
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <OrdersTable
              orders={ordersWithHighlight}
              loading={false}
              pharmacyId={pharmacyId}
              onRefresh={fetchOrders}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PastOrdersDialog;
